import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Catalogue from "@/pages/Catalogue";
import PoBuilder from "@/pages/PoBuilder";
import PurchaseOrders from "@/pages/PurchaseOrders";
import PoDetail from "@/pages/PoDetail";
import SalesOrders from "@/pages/SalesOrders";
import Inventory from "@/pages/Inventory";
import Users from "@/pages/Users";
import SapSync from "@/pages/SapSync";
import Dealers from "@/pages/Dealers";
import Settings from "@/pages/Settings";
import OutstandingPayments from "@/pages/OutstandingPayments";
import ManagerDashboard from "@/pages/ManagerDashboard";
import ProductMapping from "@/pages/ProductMapping";
import PriceListMapping from "@/pages/PriceListMapping";
import Schemes from "@/pages/Schemes";
import DealerDashboard from "@/pages/DealerDashboard";
import CustomerLedger from "@/pages/CustomerLedger";
import Invoices from "@/pages/Invoices";
import "@/App.css";

/** Redirect to login if not authenticated */
const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

/** Admin only */
const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

/** Dealer or Admin only — the only roles that can place orders */
const OrderOnly = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!["dealer", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

/** Manager, Accounts, or Admin only — management views */
const StaffOnly = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!["manager", "accounts", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

/** Admin or Accounts only */
const AccountsAdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!["accounts", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes in memory cache before refetching in background
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection time
      refetchOnWindowFocus: false, // avoid jarring refetches on window tab focus
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><AppShell /></Protected>}>
              {/* All roles */}
              <Route index element={<Dashboard />} />
              <Route path="catalogue" element={<Catalogue />} />
              <Route path="po" element={<PurchaseOrders />} />
              <Route path="po/:id" element={<PoDetail />} />
              <Route path="so" element={<SalesOrders />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="ledger" element={<CustomerLedger />} />
              <Route path="outstanding" element={<OutstandingPayments />} />
              <Route path="schemes" element={<Schemes />} />
              <Route path="settings" element={<Settings />} />

              {/* Dealer + Admin only — order placement */}
              <Route path="po/new" element={<OrderOnly><PoBuilder /></OrderOnly>} />

              {/* Staff only — management views */}
              <Route path="inventory" element={<StaffOnly><Inventory /></StaffOnly>} />
              <Route path="manager-dashboard" element={<StaffOnly><ManagerDashboard /></StaffOnly>} />
              <Route path="dealer-analytics" element={<StaffOnly><DealerDashboard /></StaffOnly>} />

              {/* Admin & Accounts only — mapping and master sync */}
              <Route path="product-mapping" element={<AccountsAdminOnly><ProductMapping /></AccountsAdminOnly>} />
              <Route path="price-list-mapping" element={<AccountsAdminOnly><PriceListMapping /></AccountsAdminOnly>} />
              <Route path="sap-sync" element={<AccountsAdminOnly><SapSync /></AccountsAdminOnly>} />

              {/* Admin only */}
              <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
              <Route path="dealers" element={<AdminOnly><Dealers /></AdminOnly>} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
